// client/src/components/OwnerMultiField.js
// Relationship Owner picker: chosen owners as removable chips plus a search
// box. Owners are individuals only, from /api/users/relationship-owners (RGA
// users and RGA Partner contacts), so unlike EntityMultiField there is no
// "+ Create". Deactivated users stay visible as chips on records they
// already own but can't be added again.
import { useEffect, useRef, useState } from 'react'

const MAX_RESULTS = 50

export default function OwnerMultiField({ owners, value, onChange, placeholder = 'Type to add an owner…' }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const boxRef = useRef(null)

  const list = owners ?? []
  const nameOf = id => list.find(o => o.id === id)?.name ?? '…'
  const q = query.trim().toLowerCase()
  const results = list
    .filter(o => !o.inactive && !value.includes(o.id))
    .filter(o => !q || o.name.toLowerCase().includes(q))
    .slice(0, MAX_RESULTS)

  useEffect(() => { setActive(0) }, [query])

  useEffect(() => {
    if (!open) return
    function onClickAway(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickAway)
    return () => document.removeEventListener('mousedown', onClickAway)
  }, [open])

  function pick(o) {
    onChange([...value, o.id])
    setQuery('')
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(a + 1, results.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter') { if (open && results[active]) { e.preventDefault(); pick(results[active]) } }
    else if (e.key === 'Escape') { setOpen(false) }
    else if (e.key === 'Backspace' && !query && value.length) { onChange(value.slice(0, -1)) }
  }

  return (
    <div>
      {value.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {value.map(id => (
            <span key={id} className="badge badge-blue" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {nameOf(id)}
              <button type="button" title="Remove" onClick={() => onChange(value.filter(v => v !== id))}
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 12 }}>✕</button>
            </span>
          ))}
        </div>
      )}
      <div ref={boxRef} style={{ position: 'relative' }}>
        <div style={{ padding: '8px 10px', background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: 6 }}>
          <input
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true) }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            style={{
              background: 'transparent', border: 'none', outline: 'none',
              color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit',
              width: '100%', padding: 0,
            }}
          />
        </div>
        {open && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 30,
            maxHeight: 260, overflowY: 'auto',
            background: 'var(--surface-raised)', border: '1px solid var(--border-light)',
            borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', padding: '4px 0',
          }}>
            {results.length === 0 ? (
              <div style={{ padding: '8px 12px', fontSize: 13, color: 'var(--text-muted)' }}>
                {q ? 'No matching RGA team member or partner' : 'Everyone is already added'}
              </div>
            ) : results.map((o, i) => (
              <div
                key={o.id}
                onMouseDown={e => { e.preventDefault(); pick(o) }}
                onMouseEnter={() => setActive(i)}
                style={{
                  padding: '7px 12px', fontSize: 13, cursor: 'pointer',
                  display: 'flex', justifyContent: 'space-between', gap: 12,
                  background: i === active ? 'var(--surface-hover)' : 'transparent',
                  color: 'var(--text-primary)',
                }}
              >
                <span>{o.name}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {o.source === 'partner' ? 'RGA Partner' : 'RGA team'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
